import { useCallback, useEffect, useRef, useState } from "react";

import { headingFromOrientation, smoothAngle } from "../utils/compass";

type CompassStatus = "idle" | "active" | "denied" | "unavailable";

/** iOS Safari exposes these outside the standard DeviceOrientationEvent typings. */
interface SafariDeviceOrientationEvent extends DeviceOrientationEvent {
  webkitCompassHeading?: number;
}
interface SafariDeviceOrientationEventConstructor {
  requestPermission?: () => Promise<PermissionState>;
}

const SMOOTHING_FACTOR = 0.25;
const FIRST_READING_TIMEOUT_MS = 1500;

const orientationEventName = () =>
  "ondeviceorientationabsolute" in window ? "deviceorientationabsolute" : "deviceorientation";

const requestOrientationPermission = async (): Promise<boolean> => {
  const { requestPermission } = DeviceOrientationEvent as SafariDeviceOrientationEventConstructor;
  if (!requestPermission) return true;
  return (await requestPermission().catch(() => "denied")) === "granted";
};

export const useCompassHeading = () => {
  const [status, setStatus] = useState<CompassStatus>("idle");
  const [heading, setHeading] = useState<number>();
  const cleanupRef = useRef<VoidFunction>(undefined);

  const release = useCallback(() => {
    cleanupRef.current?.();
    cleanupRef.current = undefined;
    setHeading(undefined);
  }, []);

  const stop = useCallback(() => {
    release();
    setStatus("idle");
  }, [release]);

  const start = useCallback(async () => {
    release();
    if (!(await requestOrientationPermission())) {
      setStatus("denied");
      return;
    }

    let smoothed: number | undefined;
    let frame: number | undefined;
    const eventName = orientationEventName();

    const handleOrientation = (event: SafariDeviceOrientationEvent) => {
      const next = headingFromOrientation(event, window.screen.orientation.angle);
      if (next === undefined) return;
      smoothed = smoothed === undefined ? next : smoothAngle(smoothed, next, SMOOTHING_FACTOR);
      frame ??= requestAnimationFrame(() => {
        frame = undefined;
        setHeading(smoothed);
      });
    };

    const timeout = window.setTimeout(() => {
      if (smoothed !== undefined) return;
      release();
      setStatus("unavailable");
    }, FIRST_READING_TIMEOUT_MS);

    window.addEventListener(eventName, handleOrientation);
    cleanupRef.current = () => {
      window.removeEventListener(eventName, handleOrientation);
      window.clearTimeout(timeout);
      if (frame !== undefined) cancelAnimationFrame(frame);
    };
    setStatus("active");
  }, [release]);

  useEffect(() => () => cleanupRef.current?.(), []);

  return { status, heading, start, stop };
};

export type { CompassStatus };
